export function resolveStudioAgentAlias(
  name: string | undefined,
  agentId: string,
): string {
  const alias = (name || agentId)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return alias || agentId;
}

/** Tree icon should spin while this fil is working (server or local turn). */
export function studioThreadTreeBusy(input: {
  threadId: string;
  attention?: string | null;
  selectedThreadId?: string | null;
  turnPending?: boolean;
}): boolean {
  if (input.attention === "running") return true;
  if (input.threadId !== input.selectedThreadId) return false;
  return Boolean(input.turnPending);
}

/** Persisted placeholder — same word as the Studio mode, not a fil name. */
export function isGenericStudioThreadTitle(title?: string | null): boolean {
  const normalized = title?.trim().toLowerCase() ?? "";
  return (
    normalized === "" ||
    normalized === "studio" ||
    normalized === "studio sandbox"
  );
}

export function formatStudioThreadOptionLabel(
  thread: { id: string; title?: string | null },
  siblings: Array<{ id: string; title?: string | null }>,
  untitledLabel = "Fil",
): string {
  const displayTitle = (item: { id: string; title?: string | null }) => {
    const raw = item.title?.trim() ?? "";
    if (isGenericStudioThreadTitle(raw)) return untitledLabel;
    return raw || item.id;
  };
  const title = displayTitle(thread);
  const duplicateTitleCount = siblings.filter(
    (candidate) => displayTitle(candidate) === title,
  ).length;
  if (duplicateTitleCount <= 1) {
    return title;
  }
  return `${title} · ${thread.id.slice(-6)}`;
}

/** Opening a fil uses the Agent that listed it, not a leftover URL host. */
export function studioThreadNavigationHost(input: {
  listingAgentId: string | null | undefined
  urlAgentId: string | null | undefined
}): string | null {
  return input.listingAgentId?.trim() || input.urlAgentId?.trim() || null
}

/** Only fetch / attach a fil that this Agent actually lists. */
export function studioThreadListedOnHost(
  threadId: string | null | undefined,
  threads: ReadonlyArray<{ id: string }>,
): boolean {
  const id = threadId?.trim() || "";
  if (!id) return false;
  return threads.some((thread) => thread.id === id);
}

/** Newest-first preview in the Studio tree. Older fils stay behind More. */
export const STUDIO_TREE_THREAD_PREVIEW = 4;

export function visibleStudioTreeThreads<T extends { id: string }>(
  threads: T[],
  options: { expanded: boolean; selectedThreadId?: string | null },
): { visible: T[]; hiddenCount: number } {
  if (options.expanded || threads.length <= STUDIO_TREE_THREAD_PREVIEW) {
    return { visible: threads, hiddenCount: 0 };
  }
  const preview = threads.slice(0, STUDIO_TREE_THREAD_PREVIEW);
  const selected = options.selectedThreadId
    ? threads.find((thread) => thread.id === options.selectedThreadId)
    : undefined;
  if (selected && !preview.some((thread) => thread.id === selected.id)) {
    return {
      visible: [...preview, selected],
      hiddenCount: threads.length - preview.length - 1,
    };
  }
  return {
    visible: preview,
    hiddenCount: threads.length - preview.length,
  };
}
